import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { isEmbedded } from "@/lib/embedded";

/**
 * The Support Hub contact widget.
 *
 * Loads support.edufocalgroup.com/widget.js, which renders its own floating button
 * inside a shadow root and posts straight into the support inbox. Support Hub decides
 * what this site may do from the request's origin, so there is no key here to leak.
 *
 * Three things this component is responsible for:
 *
 *   1. Staying out of the way. It is the usual small round launcher in the bottom-right
 *      corner, never a labelled pill over the content, and on a phone it is lifted clear
 *      of the tab bar. StudyBuddy keeps to the left so the two never share a corner.
 *      The same form also opens from the account menu (TopNavigation calls openSupport).
 *   2. Knowing who is asking. Once the user is loaded we hand their name and email to
 *      the widget so a parent is not asked to type details we already hold.
 *   3. Knowing when not to appear. A floating button over a timed test is a distraction
 *      at exactly the wrong moment, and inside the iOS web view it would be a second,
 *      web-shaped support channel in a native app.
 *
 * Mounted ONCE, in App, above the layout branches. It used to be mounted per layout,
 * and the admin layout had no copy, so navigating into /admin unmounted the only code
 * that could hide the button and it stayed on screen.
 */

const WIDGET_SRC = "https://support.edufocalgroup.com/widget.js";

declare global {
  interface Window {
    EduFocalSupport?: {
      identify: (who: { name?: string; email?: string; userId?: string }) => void;
      open: () => void;
      close: () => void;
      launcher?: (mode: "label" | "icon" | "none") => void;
      place?: (where: { position?: "left" | "right"; offsetBottom?: number }) => void;
    };
  }
}

/**
 * Routes where the button is hidden.
 *
 * Learning flows are timed or concentration-heavy and the content runs to the edges;
 * /ref is the referral-partner portal, a different audience with a different contact
 * route. Everything else (dashboard, settings, billing, parent portal, marketing) 
 * keeps it, because those are where people actually get stuck.
 */
const HIDDEN_PREFIXES = [
  "/quiz",
  "/test",
  "/challenge",
  "/performance-tasks/",
  "/lessons/",
  "/reading/",
  "/courses/",
  "/ref/",
  "/admin",
];

function hiddenOn(path: string): boolean {
  return HIDDEN_PREFIXES.some((p) => path === p.replace(/\/$/, "") || path.startsWith(p));
}

/** Layouts that return before the main tree, so they have no mobile tab bar to clear. */
const NO_TAB_BAR_PREFIXES = ["/parent", "/subscribe", "/settings", "/preferences", "/pep-check"];

/** Matches the md breakpoint MobileBottomNav disappears at. */
function usePhoneWidth(): boolean {
  const query = "(max-width: 767px)";
  const [isPhone, setIsPhone] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setIsPhone(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isPhone;
}

/** Clear of the mobile tab bar (4rem) with room to breathe; the widget adds the iOS safe-area inset. */
const TAB_BAR_LIFT = 88;

/** Open the support form from the app's own menu. Falls back if the widget never loaded. */
export function openSupport(fallback: () => void): void {
  if (window.EduFocalSupport) window.EduFocalSupport.open();
  else fallback();
}

/** The widget script is deferred, so its API may not exist yet. */
function whenWidgetReady(run: (api: NonNullable<Window["EduFocalSupport"]>) => void): () => void {
  let cancelled = false;
  let attempts = 0;
  const attempt = () => {
    if (cancelled) return;
    if (window.EduFocalSupport) return run(window.EduFocalSupport);
    if (attempts++ < 40) setTimeout(attempt, 250);
  };
  attempt();
  return () => { cancelled = true; };
}

export function SupportWidget() {
  const [location] = useLocation();
  const { user } = useAuth();
  const loadedRef = useRef(false);
  const identifiedRef = useRef<string | null>(null);

  const hidden = isEmbedded() || hiddenOn(location);
  const isPhone = usePhoneWidth();
  // The tab bar only exists for a signed-in user, on a phone, inside the main layout.
  const hasTabBar = isPhone && !!user && !NO_TAB_BAR_PREFIXES.some((p) => location.startsWith(p));
  const offsetBottom = hasTabBar ? TAB_BAR_LIFT : 20;

  // Load once, then keep it across navigations: re-injecting on every route change
  // would tear down and rebuild the widget under the user.
  useEffect(() => {
    if (hidden || loadedRef.current) return;
    if (document.querySelector(`script[src="${WIDGET_SRC}"]`)) {
      loadedRef.current = true;
      return;
    }
    const s = document.createElement("script");
    s.src = WIDGET_SRC;
    s.defer = true;
    s.setAttribute("data-label", "Support");
    s.setAttribute("data-position", "right");
    s.setAttribute("data-launcher", "icon");
    s.setAttribute("data-offset-bottom", String(offsetBottom));
    s.setAttribute("data-accent", "#6d28d9");
    s.setAttribute("data-greeting", "Tell us what's happening and we'll reply by email.");
    s.onerror = () => console.warn("[SupportWidget] could not load the support widget");
    document.body.appendChild(s);
    loadedRef.current = true;
  }, [hidden]);

  // Hand over who is signed in, once the widget exists and the user is known.
  useEffect(() => {
    if (!user) return;
    if (identifiedRef.current === user.id) return;

    return whenWidgetReady((api) => {
      api.identify({
        name: [user.firstName, user.lastName].filter(Boolean).join(" "),
        // A student's own address is the one that reaches them; billing questions
        // usually come from the parent, who signs in on their own account.
        email: user.email,
        userId: user.id,
      });
      identifiedRef.current = user.id;
    });
  }, [user]);

  // The same page session moves between layouts with and without a tab bar, so the lift
  // follows the route rather than being fixed at load.
  useEffect(() => {
    if (hidden) return;
    return whenWidgetReady((api) => api.place?.({ position: "right", offsetBottom }));
  }, [hidden, offsetBottom]);

  // Hide and show without unloading: the widget owns its own DOM, so toggling the
  // host element's visibility is cheaper and less jarring than tearing it down.
  useEffect(() => {
    if (!loadedRef.current) return;
    // Waits for the script: navigating onto a hidden route while it is still loading
    // would otherwise find no element to hide, and the button would appear there.
    return whenWidgetReady((api) => {
      const el = document.querySelector<HTMLElement>("[data-edufocal-support]");
      if (el) el.style.display = hidden ? "none" : "";
      if (hidden) api.close();
    });
  }, [hidden, location]);

  return null;
}
